import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AuthorizationAccessType } from './AuthorizationModalConsent';
import { useProxyAuthorization, getPersonaType, logoutErrorHandler, ANALYTICS_MESSAGES,
} from 'common';
import { GlobalContext, isAvMed } from 'context';

type Member = {
  memberName: string;
  memberId: string;
  mId: string;
};

type UseDependentConsentParams = {
  checkMemberAccess: (value: boolean) => void | Promise<void>;
  membersTable?: Member[];
  setLoading?: (value: boolean) => void;
};


// Shared consent flow for the "allow dependent access" checkbox.

export function useDependentConsent({
  checkMemberAccess,
  membersTable = [],
  setLoading,
}: UseDependentConsentParams) {
  const { t } = useTranslation();
  const [dependentAccess, setDependentAccess] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showConsentActionButtons, setShowConsentActionButtons] = useState(true);
  const [consentAccessType, setConsentAccessType] =
    useState<AuthorizationAccessType>('parent');
  const [showRemoveAccessModal, setShowRemoveAccessModal] = useState(false);
  const [removeAccessType, setRemoveAccessType] =
    useState<AuthorizationAccessType | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const { loginMemberId, primaryUser, getFeature } = useContext(GlobalContext);
  const { initiateProxy, terminateProxy } = useProxyAuthorization();
  const { contentListingItemClicked } = useAdobeAnalytics();
  const isCheckboxPopupEnabled = getFeature?.CheckboxPopup === true;
  const shouldUseProxyFlow = isCheckboxPopupEnabled && !isAvMed();

  const domainForSegment =
    globalThis.location?.hostname?.split('.')[0] || 'portal';

  const extractErrorCode = (error: unknown): string | undefined => {
    if (!error || typeof error !== 'object') {
      if (typeof error === 'string') {
        const match = error.match(/\b(401|440)\b/);
        return match?.[1];
      }
      return undefined;
    }
    return undefined;
  };

  const getErrorMessage = (error: unknown) => {
    if (typeof error === 'string' && error.trim()) {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: string }).message;
      if (message && message.trim()) {
        return message;
      }
    }
    return t('common.internalServerError') || '';
  };

  const isNotFoundError = (error: unknown): boolean => {
    if (typeof error === 'string') {
      return /\b404\b/.test(error);
    }

    if (error && typeof error === 'object') {
      if ('errorCode' in error) {
        const errorCode = (error as { errorCode?: unknown }).errorCode;
        if (`${errorCode}` === '404') {
          return true;
        }
      }

      if ('message' in error) {
        const message = (error as { message?: string }).message;
        if (message && /\b404\b/.test(message)) {
          return true;
        }
      }
    }

    return false;
  };

  const openErrorModal = (error: unknown) => {
    const errorCode = extractErrorCode(error);
    
    if (logoutErrorHandler(errorCode ?? error)) {
      return;
    }

    setErrorModalMessage(getErrorMessage(error));
    setShowErrorModal(true);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorModalMessage('');
  };

  // Get member name from membersTable
  const getMemberName = () => {
    const loggedInMember = membersTable.find(
      (m) => m.memberId === loginMemberId
    );
    return loggedInMember?.memberName || primaryUser || '';
  };

  const updateDependents = async (isChecked: boolean) => {
  // Call the API first; only update the checkbox once it succeeds so a
  // failure leaves the checkbox in its previous state.
  await checkMemberAccess(isChecked);
  setDependentAccess(isChecked);
};

  const openRemoveAccessModal = (type: AuthorizationAccessType) => {
    setRemoveAccessType(type);
    setShowRemoveAccessModal(true);
  };

  const handleRemoveAccessCancel = () => {
    contentListingItemClicked({
      segment: `${domainForSegment}/${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_KEEP_ACCESS,
              contentTitle: 'Keep Access',
              itemPosition: 1,
            },
          ],
        },
      },
    });
    setShowRemoveAccessModal(false);
    setRemoveAccessType(null);
  };

  const handleRemoveAccessConfirm = async () => {
    contentListingItemClicked({
      segment: `${domainForSegment}/${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_REMOVE_ACCESS,
              contentTitle: 'Remove Access',
              itemPosition: 1,
            },
          ],
        },
      },
    });
    // Close the modal first, then run the API and show the loader while it resolves.
    let requestError: unknown = null;
    setLoading?.(true);
    handleRemoveAccessCancel();
    try {
      if (shouldUseProxyFlow && loginMemberId) {
        try {
          await terminateProxy({
            designatorMemberId: loginMemberId,
          });
        } catch (proxyError) {
          // Legacy users may not have a proxy record; treat 404 as already removed.
          if (!isNotFoundError(proxyError)) {
            throw proxyError;
          }
        }
      }

      await updateDependents(false);
    } catch (error) {
      requestError = error;
    } finally {
      setLoading?.(false);
    }

    if (requestError) {
      openErrorModal(requestError);
    }
  };

  const handleConsentCancel = () => {
    contentListingItemClicked({
      segment: `${domainForSegment}/${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_CONSENT_CANCEL,
              contentTitle: 'Cancel',
              itemPosition: 1,
            },
          ],
        },
      },
    });
    setShowConsentModal(false);
    setConsentAccessType('parent');
    setShowConsentActionButtons(true);
  };

  const handleConsentConfirm = async () => {
    contentListingItemClicked({
      segment: `${domainForSegment}/${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: ANALYTICS_MESSAGES.POP_AUTHORIZATION_CONSENT,
              contentTitle: 'Consent',
              itemPosition: 1,
            },
          ],
        },
      },
    });
    // Close the modal first, then run the API and show the loader while it resolves.
    let requestError: unknown = null;
    setLoading?.(true);
    setShowConsentModal(false);
    setConsentAccessType('parent');
    setShowConsentActionButtons(true);
    try {
      if (shouldUseProxyFlow && loginMemberId && getMemberName) {
        const memberName = getMemberName();
        const personaType = getPersonaType(loginMemberId);
        
        await initiateProxy({
          designatorMemberId: loginMemberId,
          designatorName: memberName,
          personaType: personaType,
        });
      }
      await updateDependents(true);
    } catch (error) {
      requestError = error;
    } finally {
      setLoading?.(false);
    }

    if (requestError) {
      openErrorModal(requestError);
    }
  };

  const openViewTermsModal = (accessType: AuthorizationAccessType) => {
    setConsentAccessType(accessType);
    setShowConsentActionButtons(false);
    setShowConsentModal(true);
  };

  const handleDependentAccessChange = (
    isChecked: boolean,
    accessType: AuthorizationAccessType
  ) => {
    if (!shouldUseProxyFlow) {
      void (async () => {
        let requestError: unknown = null;
        setLoading?.(true);
        try {
          await updateDependents(isChecked);
        } catch (error) {
          requestError = error;
        } finally {
          setLoading?.(false);
        }

        if (requestError) {
          openErrorModal(requestError);
        }
      })();
      return;
    }

    if (isChecked) {
      setConsentAccessType(accessType);
      setShowConsentActionButtons(true);
      setShowConsentModal(true);
      return;
    }

    openRemoveAccessModal(accessType);
  };

  return {
    dependentAccess,
    setDependentAccess,
    showConsentModal,
    showConsentActionButtons,
    consentAccessType,
    showRemoveAccessModal,
    removeAccessType,
    showErrorModal,
    errorModalMessage,
    handleDependentAccessChange,
    openViewTermsModal,
    handleConsentCancel,
    handleConsentConfirm,
    handleRemoveAccessCancel,
    handleRemoveAccessConfirm,
    closeErrorModal,
    openErrorModal,
  };
}

export type UseDependentConsentReturn = ReturnType<typeof useDependentConsent>;
